import { createSignal, createEffect, For, Show, Switch, Match } from "solid-js";
import { useApi, isOk } from "../api";
import { useToast } from "./MessageBox";
import Text from "./Text";
import PingBadge from "./PingBadge";
import { useTranslation } from "../hooks/useTranslation";
import type { BotProxyAssignment } from "../services/proxyApi";
import styles from "./ProxySelector.module.css";
import { firstError } from '../api/envelope';

interface Props {
  botId: string;
  onChange?: (assignment: BotProxyAssignment) => void;
  disabled?: boolean;
}

export default function ProxySelector(props: Props) {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();
  const [assignment, setAssignment] = createSignal<BotProxyAssignment | null>(null);
  const [saving, setSaving] = createSignal(false);
  const [externalMode, setExternalMode] = createSignal(false);

  async function load(botId: string) {
    const res = await api.botRegistry.getProxyAssignment(botId);
    if (isOk(res.data)) {
      const a = res.data.value as BotProxyAssignment;
      setAssignment(a);
      setExternalMode(!!a.externalProxyId);
    }
  }

  createEffect(() => {
    const id = props.botId;
    if (!id) return;
    setAssignment(null);
    load(id);
  });

  async function setInternalProxy(proxyId: string | null) {
    setSaving(true);
    const res = await api.botRegistry.setProxy(props.botId, { proxyId });
    setSaving(false);

    if (isOk(res.data)) {
      const updated = res.data.value as BotProxyAssignment;
      setAssignment(updated);
      props.onChange?.(updated);
      toast?.info(t("PROXY_PREMIUM_ASSIGNED"));
    } else {
      toast?.error(firstError(res.data) ?? t("PROXY_PREMIUM_ASSIGN_FAILED"));
    }
  }

  async function setExternalProxy(externalProxyId: string | null) {
    setSaving(true);
    const res = await api.botRegistry.setExternalProxy(props.botId, { externalProxyId });
    setSaving(false);

    if (isOk(res.data)) {
      const updated = res.data.value as BotProxyAssignment;
      setAssignment(updated);
      props.onChange?.(updated);
      toast?.info(t("PROXY_EXTERNAL_ASSIGNED"));
    } else {
      toast?.error(firstError(res.data) ?? t("PROXY_EXTERNAL_ASSIGN_FAILED"));
    }
  }

  function handleInternalChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    const raw = target.value;
    const proxyId: string | null = raw === "__direct__" ? null : raw;
    void setInternalProxy(proxyId);
  }

  function handleExternalChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    const raw = target.value;
    const externalProxyId: string | null = raw === "__none__" ? null : raw;
    void setExternalProxy(externalProxyId);
  }

  function toggleExternalMode(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    setExternalMode(checked);
    if (!checked && assignment()?.externalProxyId) {
      void setExternalProxy(null);
    }
  }

  function currentInternalValue() {
    const a = assignment();
    if (!a) return "__direct__";
    return a.proxyId ?? "__direct__";
  }

  function currentExternalValue() {
    return assignment()?.externalProxyId ?? "__none__";
  }

  function currentPingMs(): number | null | undefined {
    const a = assignment();
    if (!a?.proxyId) return undefined;
    return a.assignable.find(p => p.id === a.proxyId)?.lastPingMs;
  }

  function showRestrictedOption(a: BotProxyAssignment) {
    return a.proxyId && !a.canReassignCurrent && !a.assignable.some(p => p.id === a.proxyId);
  }

  function externalAvailable() {
    const a = assignment();
    return !!a && a.assignableExternal.length > 0;
  }

  function externalAutoAssigned(a: BotProxyAssignment) {
    return !!a.externalProxyId && !a.canUseDirect;
  }

  function externalEnabled() {
    const a = assignment();
    return !!a && a.externalProxyEnabled;
  }

  function formatExternalLabel(p: { poolTitle: string; identity: string; countryCode: string; botsUsingCount: number }) {
    const cc = p.countryCode ? `${p.countryCode} ` : "";
    const bots = p.botsUsingCount === 1
      ? t("PROXY_EXTERNAL_OPTION_BOT")
      : t("PROXY_EXTERNAL_OPTION_BOTS", p.botsUsingCount);
    return `${p.poolTitle} · ${cc}${p.identity} · ${bots}`;
  }

  return (
    <Show when={assignment()}>
      {(a) => (
        <div class={styles.wrapper}>
          <Show when={externalEnabled() && a().canUseDirect}>
            <label class={styles.toggle}>
              <input
                type="checkbox"
                checked={externalMode()}
                onChange={toggleExternalMode}
                disabled={saving() || props.disabled}
              />
              <span><Text val="PROXY_USE_EXTERNAL" /></span>
            </label>
          </Show>

          <Show when={!externalMode()}>
            <div class={styles.row}>
              <span class={styles.label}><Text val="PROXY_LABEL" /></span>
              <select
                class={styles.select}
                value={currentInternalValue()}
                onChange={handleInternalChange}
                disabled={saving() || props.disabled}
              >
                <Show when={a().canUseDirect}>
                  <option value="__direct__">-- Direct (no proxy) --</option>
                </Show>
                <For each={a().assignable}>
                  {(p) => (
                    <option value={p.id}>
                      {p.name} {p.lastPingMs != null ? `· ${Math.round(p.lastPingMs)}ms` : "· down"}
                    </option>
                  )}
                </For>
                <Show when={showRestrictedOption(a())}>
                  <option value={a().proxyId ?? ""} disabled selected>
                    {a().proxyName} · admin-assigned
                  </option>
                </Show>
              </select>
              <Show when={a().proxyId}>
                <PingBadge ms={currentPingMs()} />
              </Show>
            </div>
            <Show when={a().proxyId && !a().canReassignCurrent}>
              <div class={styles.note}>
                <i class="fa-solid fa-lock" />
                <span><Text val="PROXY_ADMIN_ASSIGNED_NOTE" /></span>
              </div>
            </Show>
          </Show>

          <Show when={externalMode()}>
            <div class={styles.row}>
              <span class={styles.label}><Text val="PROXY_EXTERNAL_LABEL" /></span>
              <Switch>
                <Match when={externalAutoAssigned(a())}>
                  <span class={styles.note}>
                    <i class="fa-solid fa-circle-info" />
                    <Text val="PROXY_EXTERNAL_AUTO_ASSIGNED" />
                  </span>
                </Match>
                <Match when={externalAvailable()}>
                  <select
                    class={styles.select}
                    value={currentExternalValue()}
                    onChange={handleExternalChange}
                    disabled={saving() || props.disabled}
                  >
                    <option value="__none__"><Text val="PROXY_EXTERNAL_NONE" /></option>
                    <For each={a().assignableExternal}>
                      {(p) => (
                        <option value={p.id}>{formatExternalLabel(p)}</option>
                      )}
                    </For>
                  </select>
                </Match>
                <Match when={true}>
                  <span class={styles.note}>
                    <i class="fa-solid fa-circle-info" />
                    <Text val="PROXY_EXTERNAL_EMPTY" />
                  </span>
                </Match>
              </Switch>
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
}
